import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Box,
  Button,
  Icon,
  ScrollArea,
  Text,
} from "@salestrak/ui";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { PageHeader, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter } from "#app/components/page-header";
import { SettingsGroup, SettingsGroupEmptyRow, SettingsGroupHeader, SettingsGroupRow } from "#app/components/settings/settings-group";
import { SecuritySettings } from "#app/components/settings/security-settings";
import { ACCESS_ANCHORS } from "#app/components/settings-search/rows/access";
import { SettingsHighlightTarget } from "#app/components/settings-search/settings-highlight";
import { useApiKeys, useApiKeyMutations } from "#app/hooks/settings/use-api-key-mutations";
import { useAppData } from "#app/lib/data";
import { ApiKeyActionsMenu } from "#app/components/settings/api-key-actions-menu";
import { EntityTag } from "#app/components/entity";
import { getPermissionsLabel } from "#app/components/settings/permission-badge";
import { formatDate } from "#app/lib/utils/format-date";
import { ApiKeyRevokeDialog } from "#app/components/settings/api-key-revoke-dialog";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { usePermission } from "#app/lib/hooks/use-permission";
import { SettingsGroupRowsSkeleton } from "#app/components/settings/settings-page-skeleton";

// Mounted only for members holding `workspace.integrations` — every api-key
// query/mutation is gated on it, so the hooks (and their FORBIDDEN toasts)
// must not fire for anyone else. The page itself is open to all: it also
// carries account security, which every member owns.
function PersonalApiKeysSection() {
  const { push } = useAppNavigate();
  const { currentUser } = useAppData();
  const { apiKeys, total, isLoading, error, refetch } = useApiKeys(currentUser?.userId);
  const { revokeApiKey } = useApiKeyMutations();

  const [revokingKey, setRevokingKey] = useState<{ apiKeyId: string; name: string } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  async function handleRevoke() {
    if (!revokingKey) return;
    await revokeApiKey(revokingKey.apiKeyId);
  }

  if (error && apiKeys.length === 0) {
    return <LoadErrorState noun="API keys" error={error} onRetry={() => { void refetch(); }} />;
  }

  return (
    <>
      <SettingsHighlightTarget anchor={ACCESS_ANCHORS.apiKeys}>
      <SettingsGroup
        label="Personal API keys"
        subtitle="Build your own integrations with SalesTrak's GraphQL API."
      >
        {apiKeys.length > 0 ? (
          <SettingsGroupHeader>
            <Text size="sm" weight="medium">
              {total} {total === 1 ? "key" : "keys"}
            </Text>
            <Button
              aria-label="Create API key"
              variant="ghost"
              size="icon-xs"
              onClick={() => push("/settings/access/create")}
            >
              <Icon name="plus" size="md" />
            </Button>
          </SettingsGroupHeader>
        ) : null}

        {/* One row reserves the same height as the empty row, so the common
          * fast, empty resolve swaps in place with no card collapse. */}
        {isLoading && apiKeys.length === 0 ? (
          <SettingsGroupRowsSkeleton rows={1} />
        ) : null}

        {!isLoading && apiKeys.length === 0 ? (
          <SettingsGroupEmptyRow
            label="No API keys"
            actionLabel="New API key"
            onAction={() => push("/settings/access/create")}
          />
        ) : null}

        {apiKeys.map((key: any) => {
          const dateLabel = key.creationDate ? formatDate(key.creationDate) : null;
          return (
            <SettingsGroupRow
              key={key.apiKeyId}
              label={key.name}
              subtitle={`${getPermissionsLabel(key.permissions)}${dateLabel ? ` · ${dateLabel}` : ""}`}
              leading={
                <EntityTag
                  entity={{
                    kind: "user",
                    id: key.user?.userId ?? key.apiKeyId,
                    user: key.user ?? null,
                  }}
                  display="visual"
                  size="md"
                  interactive={false}
                />
              }
              onClick={() => push(`/settings/access/${key.apiKeyId}`)}
              chevron={false}
              actionsVisible={openMenuId === key.apiKeyId}
            >
              <ApiKeyActionsMenu
                onOpenChange={(open) => setOpenMenuId(open ? key.apiKeyId : null)}
                onEdit={() => push(`/settings/access/${key.apiKeyId}/edit`)}
                onRevoke={() => setRevokingKey({ apiKeyId: key.apiKeyId, name: key.name })}
              />
            </SettingsGroupRow>
          );
        })}
      </SettingsGroup>
      </SettingsHighlightTarget>

      <ApiKeyRevokeDialog
        open={revokingKey !== null}
        onOpenChange={(open) => { if (!open) setRevokingKey(null); }}
        apiKeyName={revokingKey?.name ?? ""}
        onConfirm={handleRevoke}
      />
    </>
  );
}

function AccessPage() {
  const canManageIntegrations = usePermission("workspace.integrations");

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderTitle>Security & access</PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="2xl" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            <SecuritySettings />

            {canManageIntegrations ? <PersonalApiKeysSection /> : null}

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/access/")({
  component: AccessPage,
});
